import React from 'react';
import styled from 'styled-components';
import { KpiType } from '../../types/drilldown';
import { Colors, Spacing, FontSizes, FontWeights, BorderRadius, Shadows } from '../../constants/theme';

interface KpiDrillDownCardProps {
  kpi: KpiType;
  title: string;
  value: string | number;
  unit?: string;
  change?: number;
  changeType?: 'increase' | 'decrease' | 'neutral';
  status?: 'good' | 'warning' | 'critical';
  icon?: string;
}

const CardContainer = styled.div<{ isClicked?: boolean }>`
  background: ${Colors.surface};
  border: 1px solid ${Colors.border};
  border-radius: ${BorderRadius.md}px;
  padding: ${Spacing.xl}px;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;

  &:hover {
    border-color: ${Colors.borderAccent};
    box-shadow: ${Shadows.lg};
    transform: translateY(-2px);
  }

  ${props => props.isClicked && `
    transform: scale(0.98);
    box-shadow: ${Shadows.sm};
    border-color: ${Colors.secondary};
  `}

  &::after {
    content: '🔍';
    position: absolute;
    top: ${Spacing.md}px;
    right: ${Spacing.md}px;
    font-size: ${FontSizes.lg}px;
    opacity: 0;
    transition: opacity 0.2s ease;
  }

  &:hover::after {
    opacity: 1;
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.sm}px;
  margin-bottom: ${Spacing.lg}px;
`;

const KpiIcon = styled.div<{ kpi: KpiType }>`
  width: 40px;
  height: 40px;
  border-radius: ${BorderRadius.sm}px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${FontSizes.xl}px;
  background: ${props => {
    switch (props.kpi) {
      case 'totalEmployees': return Colors.dataViz.primary;
      case 'attritionRisk': return Colors.dataViz.series5;
      case 'engagementScore': return Colors.dataViz.series2;
      case 'leadershipReady': return Colors.dataViz.series6;
      case 'timeToFill': return Colors.dataViz.series3;
      case 'diversityScore': return Colors.dataViz.series7;
      default: return Colors.secondary;
    }
  }};
  color: white;
`;

const CardTitle = styled.h3`
  font-size: ${FontSizes.lg}px;
  font-weight: 600;
  color: ${Colors.textPrimary};
  margin: 0;
  line-height: 1.4;
`;

const ValueContainer = styled.div`
  display: flex;
  align-items: baseline;
  gap: ${Spacing.xs}px;
  margin-bottom: ${Spacing.md}px;
`;

const MainValue = styled.div<{ status?: 'good' | 'warning' | 'critical' }>`
  font-size: ${FontSizes.hero}px;
  font-weight: 800;
  color: ${props => {
    switch (props.status) {
      case 'good': return Colors.successDark;
      case 'warning': return Colors.warningDark;
      case 'critical': return Colors.dangerDark;
      default: return Colors.textPrimary;
    }
  }};
  line-height: 1;
`;

const ValueUnit = styled.span`
  font-size: ${FontSizes.lg}px;
  color: ${Colors.textSecondary};
  font-weight: 400;
`;

const ChangeIndicator = styled.div<{ changeType: 'increase' | 'decrease' | 'neutral' }>`
  display: inline-flex;
  align-items: center;
  gap: ${Spacing.xs}px;
  font-size: ${FontSizes.sm}px;
  font-weight: ${FontWeights.bold};
  padding: 6px 12px;
  border-radius: 6px;
  margin-top: ${Spacing.sm}px;
  border: 1px solid ${props => {
    switch (props.changeType) {
      case 'increase': return 'rgba(21, 128, 61, 0.2)';
      case 'decrease': return 'rgba(153, 27, 27, 0.2)';
      case 'neutral': return 'rgba(51, 65, 85, 0.15)';
      default: return 'rgba(51, 65, 85, 0.15)';
    }
  }};
  background: ${props => {
    switch (props.changeType) {
      case 'increase': return 'rgba(21, 128, 61, 0.1)';
      case 'decrease': return 'rgba(153, 27, 27, 0.1)';
      case 'neutral': return 'rgba(51, 65, 85, 0.08)';
      default: return 'rgba(51, 65, 85, 0.08)';
    }
  }};
  color: ${props => {
    switch (props.changeType) {
      case 'increase': return Colors.successDark;
      case 'decrease': return Colors.dangerDark;
      case 'neutral': return Colors.textPrimary;
      default: return Colors.textPrimary;
    }
  }};
  transition: all 0.2s ease;
  
  &:hover {
    border-color: ${props => {
      switch (props.changeType) {
        case 'increase': return 'rgba(21, 128, 61, 0.3)';
        case 'decrease': return 'rgba(153, 27, 27, 0.3)';
        case 'neutral': return 'rgba(51, 65, 85, 0.2)';
        default: return 'rgba(51, 65, 85, 0.2)';
      }
    }};
    background: ${props => {
      switch (props.changeType) {
        case 'increase': return 'rgba(21, 128, 61, 0.15)';
        case 'decrease': return 'rgba(153, 27, 27, 0.15)';
        case 'neutral': return 'rgba(51, 65, 85, 0.1)';
        default: return 'rgba(51, 65, 85, 0.1)';
      }
    }};
  }
`;

const ChangeIcon = styled.span`
  font-size: ${FontSizes.md}px;
`;

const DrillDownHint = styled.div`
  position: absolute;
  bottom: ${Spacing.md}px;
  left: ${Spacing.xl}px;
  right: ${Spacing.xl}px;
  font-size: ${FontSizes.xs}px;
  color: ${Colors.textTertiary};
  text-align: center;
  opacity: 0.6;
  transition: opacity 0.2s ease;
  background: ${Colors.surface}f0;
  padding: ${Spacing.xs}px;
  border-radius: ${BorderRadius.xs}px;

  ${CardContainer}:hover & {
    opacity: 1;
    color: ${Colors.secondary};
    font-weight: 500;
  }
`;

const getKpiIcon = (kpi: KpiType): string => {
  switch (kpi) {
    case 'totalEmployees': return '👥';
    case 'attritionRisk': return '⚠️';
    case 'engagementScore': return '💡';
    case 'leadershipReady': return '🎯';
    case 'timeToFill': return '⏱️';
    case 'diversityScore': return '🌈';
    default: return '📊';
  }
};

const getChangeIcon = (changeType: 'increase' | 'decrease' | 'neutral') => {
  switch (changeType) {
    case 'increase': return '↗';
    case 'decrease': return '↘';
    case 'neutral': return '→';
    default: return '→';
  }
};

const formatValue = (value: string | number): string => {
  if (typeof value === 'string') return value;
  
  if (value >= 1000000) {
    return (value / 1000000).toFixed(1) + 'M';
  }
  
  if (value >= 1000) {
    return (value / 1000).toFixed(1) + 'K';
  }
  
  return value.toLocaleString();
};

export const KpiDrillDownCard: React.FC<KpiDrillDownCardProps> = ({
  kpi,
  title,
  value,
  unit = '',
  change,
  changeType = 'neutral',
  status,
  icon
}) => {
  return (
    <CardContainer 
      role="button"
      tabIndex={0}
      aria-label={`Drill down into ${title} details`}
    >
      <CardHeader>
        <KpiIcon kpi={kpi}>
          {icon || getKpiIcon(kpi)}
        </KpiIcon>
        <CardTitle>{title}</CardTitle>
      </CardHeader>

      <ValueContainer>
        <MainValue status={status}>
          {formatValue(value)}
        </MainValue>
        {unit && <ValueUnit>{unit}</ValueUnit>}
      </ValueContainer>

      {change !== undefined && (
        <ChangeIndicator changeType={changeType}>
          <ChangeIcon>
            {getChangeIcon(changeType)}
          </ChangeIcon>
          {Math.abs(change).toFixed(1)}{unit === '%' ? 'pp' : unit} vs last period
        </ChangeIndicator>
      )}

      <DrillDownHint>
        Click to drill down and explore details
      </DrillDownHint>
    </CardContainer>
  );
};